'use client';

import { useState } from 'react';
import SearchBar from 'components/controls/search-bar';
import MinimalHeader from 'components/headers/minimal-header';
import sum from 'lodash/sum';
import AlphabetFilters from 'components/alphabet-filters';
import EntriesByLetter from 'components/entries-by-letter';
import Breadcrumbs from 'components/breadcrumbs';
import styles from 'src/app/[locale]/(navigation)/research-centers/research-styles/index.module.scss';
import ActiveFilter from 'components/controls/active-filter';
import Subsection from 'components/containers/sub-section';
import { Link } from 'src/navigation';
import { removeDiacritics } from 'helpers/helpers';

const filterByLetter = (filteredListOfCollectors, letter) => {
	const result = {};
	if (filteredListOfCollectors[letter]) {
		result[letter] = filteredListOfCollectors[letter];
	}
	return result;
};

const NoResultsFound = ({ query }: { query: string }) => {
	return (
		<section className={styles.maipContainer}>
			<h4 className={styles.maipNoResultsTitle}>
				No results found{query && ` for "${query}"`}
			</h4>
			<p>
				The index is regularly updated and expanded. Have a suggestion for a
				gallery or individual who should be in the index?{' '}
				<Link
					className={styles.maipLink}
					href="https://airtable.com/shrX7dbAcTwh5nfVX"
				>
					Suggest an entry
				</Link>
				.
			</p>
		</section>
	);
};

const MAIPClientComponent = ({
	entriesByAlphabet,
	description,
	heading,
}: IProps) => {
	const [query, setQuery] = useState('');
	const [letterFilter, setLetterFilter] = useState(null);
	const handleSearchQueryChange = (event) => {
		setQuery(event.target.value.trim());
	};

	const handleLetterFilterChange = (event) => {
		if (event.target.checked) {
			setLetterFilter(event.target.value);
		} else {
			setLetterFilter(null);
		}
	};

	const filterListByQuery = () => {
		const result = Object.keys(entriesByAlphabet).reduce((results, letter) => {
			// search each letter's entries for query matches
			const matches = entriesByAlphabet[letter].filter((entry) => {
				// the match can be in the "indexEntry" (name) or the "lifetime" "descriptor"
				const searchText = removeDiacritics(
					`${entry.indexEntry} ${entry.lifetimeDescriptor}`.toLowerCase(),
				);
				return searchText.includes(removeDiacritics(query).toLowerCase());
			});
			if (matches.length) {
				results[letter] = matches;
			}
			return results;
		}, {});

		return result;
	};

	let filteredListOfCollectors = entriesByAlphabet;

	if (query) {
		filteredListOfCollectors = filterListByQuery();
	}

	if (letterFilter) {
		filteredListOfCollectors = filterByLetter(
			filteredListOfCollectors,
			letterFilter,
		);
	}

	const resultsCount = sum(
		Object.values(filteredListOfCollectors).map(
			(array) => (array as any[]).length,
		),
	);

	return (
		<>
			<section className={styles.maipMastHead}>
				<Breadcrumbs />

				<div className={styles.maipHeader}>
					<MinimalHeader heading={heading} description={description} />
				</div>
				<Subsection>
					<div className={styles.maipControls}>
						<legend className={styles.legend}>Search by name</legend>
						<div className={styles.maipSearch}>
							<SearchBar
								placeholderText="Search by name"
								initialQuery={query}
								onChange={handleSearchQueryChange}
							/>
						</div>
						<AlphabetFilters
							key={letterFilter}
							defaultSelected={letterFilter}
							availableLetters={Object.keys(entriesByAlphabet)}
							onChange={handleLetterFilterChange}
						/>
					</div>
				</Subsection>
			</section>
			<div className={styles.maipActiveFilters}>
				{letterFilter && (
					<ActiveFilter
						value={letterFilter}
						onClick={handleLetterFilterChange}
					/>
				)}
			</div>
			{resultsCount > 0 ? (
				<section className={styles.maipContainer}>
					<div className={styles.maipResults}>
						Showing {resultsCount} {resultsCount > 1 ? 'entries' : 'entry'}
						{query && ` for "${query}"`}
					</div>
					{Object.keys(filteredListOfCollectors).map((letter) => {
						return (
							<EntriesByLetter
								entries={filteredListOfCollectors[letter]}
								letter={letter}
								key={letter}
							/>
						);
					})}
				</section>
			) : (
				<NoResultsFound query={query} />
			)}
		</>
	);
};

export default MAIPClientComponent;

interface IProps {
	entriesByAlphabet: any;
	description: any;
	heading: string;
}
